import { SegmentedControl } from '@mantine/core';

import { useCatalogLabels } from '../labels';

interface ViewModeToggleProps {
    value: 'grid' | 'list';
    onChange: (value: 'grid' | 'list') => void;
}

// Icon-only options, matching the toolbar's other icon buttons (filter, columns, ...) - a text
// label per option ("Liste"/"Kacheln") took up more room than the icon alone needs (see Fachbereich
// feedback: "Neben den Radio Buttons nur die Icons ... zeigen ... Spart Platz."). The group's own
// "Ansicht" caption is gone too (later feedback: it cost a line of height in the toolbar for a
// control the icons already explain); the label lives on as the control's aria-label and each
// option's own aria-label/tooltip, so nothing is lost for screen readers or on hover.
// The icon `<span>` itself is deliberately left untouched (same bare
// `material-symbols-outlined` + inline font-size as every other icon in the app, e.g. Toolbar's
// filter/tune icon) - putting Tailwind's `flex` utility directly on it previously overrode the
// package's own required `display: inline-block` (material-symbols/outlined.css loads before
// Tailwind's utilities in App.css, so `.flex` won, silently breaking the ligature glyph and
// leaving the icon blank). Centering instead happens on this plain OUTER wrapper, one level up.
const IconLabel = ({ label, name }: { label: string; name: string }) => (
    <span aria-label={label} className="flex items-center" role="img" title={label}>
        <span className="material-symbols-outlined" style={{ fontSize: 20 }} aria-hidden>
            {name}
        </span>
    </span>
);

/** Kachel/Liste switch - shared by the catalog Toolbar and the Merkliste (WishlistPage), which
 * needs the same grid/list choice for its own product section without pulling in Toolbar's
 * unrelated search/sort/document-mode controls. Liste comes first (the catalog's own default,
 * see viewModeAtom).
 *
 * A SegmentedControl, not the Radio.Group with icon labels this used to be: that rendered a radio
 * DOT next to each icon inside a thin box, i.e. four markers for two states, and the two states
 * looked nearly identical (see design review, point 2 - "liest sich als kaputte Kontrollgruppe").
 * A SegmentedControl carries exactly the same semantics - one of n selected - but the highlighted
 * segment IS the state, so no second marker is needed. */
export const ViewModeToggle = ({ value, onChange }: ViewModeToggleProps) => {
    const { L } = useCatalogLabels();
    return (
        <SegmentedControl
            aria-label={L.viewMode}
            // Two classes, two separate problems, both from @uag/doka-design (see App.css):
            // `pih-inline-segmented` stops the control filling the whole row (the package renders
            // SegmentedControl at width:100%, which is right in a column but not in a toolbar), and
            // `pih-icon-segmented` gives the two glyphs the padding that the package's text-sized
            // label spacing does not - without it they end up shoulder to shoulder.
            className="pih-inline-segmented pih-icon-segmented"
            data={[
                { value: 'list', label: <IconLabel label={L.viewList} name="view_list" /> },
                { value: 'grid', label: <IconLabel label={L.viewGrid} name="grid_view" /> },
            ]}
            size="sm"
            value={value}
            onChange={(next) => onChange(next as 'grid' | 'list')}
        />
    );
};
